/**
 * Cross-game meta layer for The Lounge.
 *
 * Holds the shared Focus Points economy, lifetime statistics, achievements,
 * and the sound-effect hook. Every game (Focus Drive, Mind Over City, Lock In)
 * writes to the same store, so a single Stats/Achievements dashboard can read
 * everything back.
 *
 * All data lives in localStorage under the `lounge_meta_v1` key so it survives
 * refresh. If you ever change the schema, bump the version key.
 *
 * SECURITY / DATA-CLASSIFICATION NOTE (code review 2026-02-14):
 *   The keys used here (`lounge_meta_v1`, `lounge_sfx_muted`) hold ONLY
 *   client-side game state: Focus Points, achievements, level stars, best
 *   scores, sound-mute flag. Zero PII, zero auth tokens, zero journal/chat
 *   content. Sensitive credentials live in httpOnly cookies (bp_access /
 *   bp_refresh) which are not reachable from JS. localStorage is the correct
 *   store for durable game progression across sessions and is not an XSS
 *   token-theft vector here.
 */

const STORE_KEY = 'lounge_meta_v1';

const defaultStore = () => ({
  fp: 0,                      // Focus Points (currency)
  achievements: {},           // { [achId]: earnedIsoDate }
  runs: [],                   // last-50 recent runs, newest first
  stats: {
    lifetimeDistance: 0,      // metres (focus drive) OR pipes ×10 (flyer proxy)
    lifetimeTokens: 0,        // focus drive tokens + flyer pipes
    totalRuns: 0,
    perfectRuns: 0,
    nearMisses: 0,
    highestFlyerLevel: 1,
    highestDriveLevel: 1,
    bestFlyerScore: 0,
    bestDriveDistance: 0,
    breathCycles: 0,
  },
  // ── Mind Over City campaign progression ──
  moc: {
    lastLevel: 1,             // resume point
    highestUnlocked: 1,       // max level ever unlocked (1..30)
    levelStars: {},           // { [levelId]: 0|1|2|3 } best star count
    levelBest: {},            // { [levelId]: { orbs, nearMisses, at } }
    endlessBest: 0,           // best Endless Flow score
    masterBest: 0,            // best Master Flow score
    modesUnlocked: {
      endless: false,
      master: false,
    },
  },
  // ── Focus Drive campaign progression (Brotherhood Arcade shared shape) ──
  fd: {
    lastLevel: 1,
    highestUnlocked: 1,       // max level ever unlocked (1..100)
    levelStars: {},           // { [levelId]: 0|1|2|3 }
    levelBest: {},            // { [levelId]: { tokens, distance, nearMisses, at } }
    masterBest: 0,            // best Master Drive distance
    modesUnlocked: {
      master: false,
    },
    garage: {
      currentSkin: 'gold',
      currentTrail: 'none',
      skinsUnlocked: ['gold'],
      trailsUnlocked: ['none'],
    },
  },
  // ── Peace Defender campaign progression (Asteroids-style Lounge game) ──
  pd: {
    lastLevel: 1,
    highestUnlocked: 1,       // max level ever unlocked (1..30)
    levelStars: {},           // { [levelId]: 0|1|2|3 }
    levelBest: {},            // { [levelId]: { hits, livesLeft, at } }
    endlessBest: 0,           // Endless Peace best (kills)
    masterBest: 0,            // Master Peace best (kills)
    modesUnlocked: {
      endless: false,
      master: false,
    },
    hangar: {
      currentShip: 'arrow',
      shipsUnlocked: ['arrow'],
    },
  },
  // ── Calm Command campaign progression (Missile Command-style) ──
  cc: {
    lastLevel: 1,
    highestUnlocked: 1,
    levelStars: {},           // { [levelId]: 0|1|2|3 }
    levelBest: {},            // { [levelId]: { anchorsLeft, neutralized, at } }
    endlessBest: 0,           // Endless Calm best (missiles neutralized)
  },
  // ── Cross-game Daily / Weekly Challenges ──
  challenges: {
    daily: null,              // { key: 'YYYY-MM-DD', id, progress, target, completed, rewardClaimed }
    weekly: null,             // { key: 'YYYY-Wxx', id, progress, target, completed, rewardClaimed }
  },
});

function read() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return defaultStore();
    return { ...defaultStore(), ...JSON.parse(raw) };
  } catch { return defaultStore(); }
}
function write(s) {
  localStorage.setItem(STORE_KEY, JSON.stringify(s));
  window.dispatchEvent(new Event('lounge-meta:change'));
}
export function getMeta() { return read(); }
// Fires when a level is genuinely unlocked for the first time (frontier advanced),
// so a shared UI flourish can celebrate it. Payload: { game, level }.
function emitLevelUnlock(game, level) {
  try { window.dispatchEvent(new CustomEvent('lounge:level-unlocked', { detail: { game, level } })); } catch { /* noop */ }
}
export function subscribe(cb) {
  const on = () => cb(read());
  window.addEventListener('lounge-meta:change', on);
  window.addEventListener('storage', on);
  return () => {
    window.removeEventListener('lounge-meta:change', on);
    window.removeEventListener('storage', on);
  };
}

/* ----- Focus Points ----- */
export function addFP(amount, reason) {
  const s = read();
  s.fp = Math.max(0, s.fp + Math.floor(amount));
  write(s);
  return { total: s.fp, delta: Math.floor(amount), reason };
}

/**
 * Record a run.  gameId ∈ {flyer, drive, breath}.
 * result = { level, score, tokens, distance, perfect, nearMisses, cleared }
 * Returns { fpEarned, achievementsUnlocked: [Achievement] }
 */
export function recordRun(gameId, result) {
  const s = read();
  const now = new Date().toISOString();
  const distance = result.distance ?? 0;
  const tokens   = result.tokens ?? 0;
  const score    = result.score ?? 0;
  const level    = result.level ?? 1;
  const perfect  = !!result.perfect;
  const nearMisses = result.nearMisses ?? 0;
  const cleared  = !!result.cleared;

  // Focus Points award
  let fpEarned = 0;
  if (gameId === 'drive') {
    fpEarned += Math.floor(distance / 5);
    fpEarned += tokens * 10;
    fpEarned += nearMisses * 3;
    if (perfect) fpEarned += 25;
    if (cleared) fpEarned += 50 + level * 5;
  } else if (gameId === 'flyer') {
    fpEarned += score * 8;
    if (perfect) fpEarned += 25;
    if (cleared) fpEarned += 50 + level * 5;
  } else if (gameId === 'defender') {
    // Peace Defender: score = asteroid kills, tokens = power-ups picked
    fpEarned += score * 4;
    fpEarned += tokens * 12;
    if (perfect) fpEarned += 30;                 // flawless (no hits)
    if (cleared) fpEarned += 60 + level * 6;
  } else if (gameId === 'command') {
    // Calm Command: score = missiles neutralized, tokens = anchors surviving
    fpEarned += score * 5;
    fpEarned += tokens * 15;
    if (perfect) fpEarned += 40;                 // flawless (all anchors alive)
    if (cleared) fpEarned += 60 + level * 6;
  } else if (gameId === 'breath') {
    fpEarned += 15 * (result.cycles ?? 0);
  }
  s.fp += fpEarned;

  // Stats
  s.stats.totalRuns += 1;
  if (perfect) s.stats.perfectRuns += 1;
  s.stats.nearMisses += nearMisses;
  s.stats.lifetimeDistance += distance;
  s.stats.lifetimeTokens += tokens + score;
  if (gameId === 'flyer') {
    s.stats.highestFlyerLevel = Math.max(s.stats.highestFlyerLevel, level + (cleared ? 1 : 0));
    s.stats.bestFlyerScore = Math.max(s.stats.bestFlyerScore, score);
  }
  if (gameId === 'drive') {
    s.stats.highestDriveLevel = Math.max(s.stats.highestDriveLevel, level + (cleared ? 1 : 0));
    s.stats.bestDriveDistance = Math.max(s.stats.bestDriveDistance, distance);
  }
  if (gameId === 'defender') {
    s.stats.highestDefenderLevel = Math.max(s.stats.highestDefenderLevel ?? 1, level + (cleared ? 1 : 0));
    s.stats.bestDefenderScore = Math.max(s.stats.bestDefenderScore ?? 0, score);
  }
  if (gameId === 'command') {
    s.stats.highestCommandLevel = Math.max(s.stats.highestCommandLevel ?? 1, level + (cleared ? 1 : 0));
    s.stats.bestCommandScore = Math.max(s.stats.bestCommandScore ?? 0, score);
  }
  if (gameId === 'breath') {
    s.stats.breathCycles += (result.cycles ?? 0);
  }

  // Recent runs (last 50)
  s.runs.unshift({ id: cryptoRandomId(), gameId, at: now, ...result, fpEarned });
  s.runs = s.runs.slice(0, 50);

  // Achievements check
  const newly = ACHIEVEMENTS.filter(a => !s.achievements[a.id] && a.check(s.stats, s.runs, result, gameId));
  for (const a of newly) s.achievements[a.id] = now;

  write(s);

  // Advance active challenges outside the same write so cadence is separate & idempotent
  try {
    if (gameId === 'flyer') {
      const orbs = (result.orbs ?? result.score ?? 0);
      advanceChallenge('moc', orbs);
      if (result.cleared)  advanceChallenge('moc', 0); // no-op; kept for future
      // Track perfect (3-star) & cleared meta targets separately via _byId
      if (result.cleared) _advanceById('daily-clear-2', 1);
      if (result.cleared) _advanceById('weekly-clear-10', 1);
      if (result.stars === 3) _advanceById('daily-perfect-1', 1);
      if (result.stars === 3) _advanceById('weekly-perfect-4', 1);
    } else if (gameId === 'drive') {
      advanceChallenge('drive', result.distance ?? 0);
    } else if (gameId === 'defender') {
      advanceChallenge('defender', result.score ?? 0);
    } else if (gameId === 'command') {
      advanceChallenge('command', result.score ?? 0);
    } else if (gameId === 'breath') {
      advanceChallenge('breath', result.cycles ?? 0);
    }
  } catch { /* never let SFX/challenges break a run */ }

  return { fpEarned, achievementsUnlocked: newly };
}

/** Bump a specific challenge by id (used when target metric doesn't map cleanly to scope+metric). */
function _advanceById(id, amount) {
  const s = read();
  ['daily', 'weekly'].forEach(k => {
    const c = s.challenges[k];
    if (c && c.id === id && !c.completed) {
      c.progress = Math.min(c.target, (c.progress ?? 0) + amount);
      if (c.progress >= c.target) c.completed = true;
    }
  });
  write(s);
}

function cryptoRandomId() {
  try { return crypto.randomUUID(); } catch { return String(Date.now() + Math.random()); }
}

/* ----- Mind Over City campaign helpers ----- */
export function getMoC() { return read().moc; }

/**
 * Update campaign progress after a MoC campaign run.
 * @param {number} levelId  1..30
 * @param {object} run      { stars, orbs, nearMisses, cleared }
 */
export function saveMoCLevelResult(levelId, run) {
  const s = read();
  const prevStars = s.moc.levelStars[levelId] ?? 0;
  const newStars = Math.max(prevStars, run.stars ?? 0);
  s.moc.levelStars[levelId] = newStars;
  // Track best orbs+near-misses even if user re-runs and does worse
  const prevBest = s.moc.levelBest[levelId];
  if (!prevBest || (run.orbs ?? 0) > (prevBest.orbs ?? 0)) {
    s.moc.levelBest[levelId] = { orbs: run.orbs ?? 0, nearMisses: run.nearMisses ?? 0, at: new Date().toISOString() };
  }
  if (run.cleared) {
    {
      const _prev = s.moc.highestUnlocked;
      const _next = Math.max(_prev, Math.min(30, levelId + 1));
      s.moc.highestUnlocked = _next;
      if (_next > _prev) emitLevelUnlock('mind-over-city', _next);
    }
    if (s.moc.highestUnlocked > 15) s.moc.modesUnlocked.endless = true;
    if (s.moc.highestUnlocked > 30 || levelId === 30) s.moc.modesUnlocked.master = true;
  }
  s.moc.lastLevel = levelId;
  write(s);
  return s.moc;
}

export function setMoCLastLevel(levelId) {
  const s = read();
  s.moc.lastLevel = Math.max(1, Math.min(30, levelId));
  write(s);
}

export function saveMoCEndless(score, mode = 'endless') {
  const s = read();
  if (mode === 'master') s.moc.masterBest = Math.max(s.moc.masterBest, score);
  else s.moc.endlessBest = Math.max(s.moc.endlessBest, score);
  write(s);
}

/* ----- Focus Drive campaign helpers (Brotherhood Arcade parity) ----- */
export function getFD() { return read().fd; }

export function saveFDLevelResult(levelId, run, garageUnlockIds = []) {
  const s = read();
  const prevStars = s.fd.levelStars[levelId] ?? 0;
  const newStars = Math.max(prevStars, run.stars ?? 0);
  s.fd.levelStars[levelId] = newStars;
  const prevBest = s.fd.levelBest[levelId];
  if (!prevBest || (run.tokens ?? 0) > (prevBest.tokens ?? 0)) {
    s.fd.levelBest[levelId] = {
      tokens: run.tokens ?? 0, distance: run.distance ?? 0,
      nearMisses: run.nearMisses ?? 0, at: new Date().toISOString(),
    };
  }
  if (run.cleared) {
    {
      const _prev = s.fd.highestUnlocked;
      const _next = Math.max(_prev, Math.min(100, levelId + 1));
      s.fd.highestUnlocked = _next;
      if (_next > _prev) emitLevelUnlock('focus-drive', _next);
    }
    if (s.fd.highestUnlocked > 100 || levelId === 100) s.fd.modesUnlocked.master = true;
  }
  s.fd.lastLevel = levelId;
  // Auto-unlock any garage items the caller says are now unlocked (skins / trails at L5/L10/L20)
  for (const id of garageUnlockIds) {
    if (id.startsWith('skin:') && !s.fd.garage.skinsUnlocked.includes(id.slice(5))) {
      s.fd.garage.skinsUnlocked.push(id.slice(5));
    } else if (id.startsWith('trail:') && !s.fd.garage.trailsUnlocked.includes(id.slice(6))) {
      s.fd.garage.trailsUnlocked.push(id.slice(6));
    }
  }
  write(s);
  return s.fd;
}

export function setFDLastLevel(levelId) {
  const s = read();
  s.fd.lastLevel = Math.max(1, Math.min(100, levelId));
  write(s);
}

export function saveFDMaster(distance) {
  const s = read();
  s.fd.masterBest = Math.max(s.fd.masterBest, Math.floor(distance));
  write(s);
}

export function equipFDSkin(skinId) {
  const s = read();
  if (!s.fd.garage.skinsUnlocked.includes(skinId)) return false;
  s.fd.garage.currentSkin = skinId;
  write(s);
  return true;
}
export function equipFDTrail(trailId) {
  const s = read();
  if (!s.fd.garage.trailsUnlocked.includes(trailId)) return false;
  s.fd.garage.currentTrail = trailId;
  write(s);
  return true;
}

/* ----- Peace Defender campaign helpers (Asteroids-style) ----- */
export function getPD() { return read().pd; }

export function savePDLevelResult(levelId, run) {
  const s = read();
  const prevStars = s.pd.levelStars[levelId] ?? 0;
  const newStars = Math.max(prevStars, run.stars ?? 0);
  s.pd.levelStars[levelId] = newStars;
  const prevBest = s.pd.levelBest[levelId];
  const hits = run.hits ?? 0;
  const livesLeft = run.livesLeft ?? 0;
  if (!prevBest || hits < (prevBest.hits ?? 999) || (hits === prevBest.hits && livesLeft > (prevBest.livesLeft ?? 0))) {
    s.pd.levelBest[levelId] = { hits, livesLeft, at: new Date().toISOString() };
  }
  if (run.cleared) {
    {
      const _prev = s.pd.highestUnlocked;
      const _next = Math.max(_prev, Math.min(30, levelId + 1));
      s.pd.highestUnlocked = _next;
      if (_next > _prev) emitLevelUnlock('peace-defender', _next);
    }
    if (s.pd.highestUnlocked > 15) s.pd.modesUnlocked.endless = true;
    if (levelId === 30) s.pd.modesUnlocked.master = true;
  }
  s.pd.lastLevel = levelId;
  write(s);
  return s.pd;
}

export function setPDLastLevel(levelId) {
  const s = read();
  s.pd.lastLevel = Math.max(1, Math.min(30, levelId));
  write(s);
}

export function savePDEndless(score, mode = 'endless') {
  const s = read();
  if (mode === 'master') s.pd.masterBest = Math.max(s.pd.masterBest, score);
  else s.pd.endlessBest = Math.max(s.pd.endlessBest, score);
  write(s);
}

/**
 * Attempt to unlock a Peace Defender ship.
 * Requires: level clear >= ship.levelRequired AND FP >= ship.fpCost.
 * Spends the FP cost on success.
 */
export function unlockPDShip(ship) {
  const s = read();
  if (s.pd.hangar.shipsUnlocked.includes(ship.id)) {
    return { ok: false, reason: 'already-owned' };
  }
  const highestCleared = s.pd.highestUnlocked - 1; // highestUnlocked is next-unlocked
  if (highestCleared < ship.levelRequired) {
    return { ok: false, reason: 'level-locked', required: ship.levelRequired };
  }
  if (s.fp < ship.fpCost) {
    return { ok: false, reason: 'not-enough-fp', required: ship.fpCost, have: s.fp };
  }
  s.fp = Math.max(0, s.fp - ship.fpCost);
  s.pd.hangar.shipsUnlocked.push(ship.id);
  write(s);
  return { ok: true, fpSpent: ship.fpCost };
}

export function equipPDShip(shipId) {
  const s = read();
  if (!s.pd.hangar.shipsUnlocked.includes(shipId)) return false;
  s.pd.hangar.currentShip = shipId;
  write(s);
  return true;
}

/* ----- Calm Command campaign helpers (Missile Command-style) ----- */
export function getCC() { return read().cc; }

export function savePDCommandLevelResult() { /* legacy stub */ }

export function saveCCLevelResult(levelId, run) {
  const s = read();
  const prevStars = s.cc.levelStars[levelId] ?? 0;
  const newStars = Math.max(prevStars, run.stars ?? 0);
  s.cc.levelStars[levelId] = newStars;
  const prevBest = s.cc.levelBest[levelId];
  const anchorsLeft = run.anchorsLeft ?? 0;
  const neutralized = run.neutralized ?? 0;
  if (!prevBest || anchorsLeft > (prevBest.anchorsLeft ?? 0)
      || (anchorsLeft === prevBest.anchorsLeft && neutralized > (prevBest.neutralized ?? 0))) {
    s.cc.levelBest[levelId] = { anchorsLeft, neutralized, at: new Date().toISOString() };
  }
  if (run.cleared) {
    {
      const _prev = s.cc.highestUnlocked;
      const _next = Math.max(_prev, Math.min(30, levelId + 1));
      s.cc.highestUnlocked = _next;
      if (_next > _prev) emitLevelUnlock('calm-command', _next);
    }
  }
  s.cc.lastLevel = levelId;
  write(s);
  return s.cc;
}

export function setCCLastLevel(levelId) {
  const s = read();
  s.cc.lastLevel = Math.max(1, Math.min(30, levelId));
  write(s);
}

export function saveCCEndless(score) {
  const s = read();
  s.cc.endlessBest = Math.max(s.cc.endlessBest, score);
  write(s);
}

/* ----- Daily / Weekly Challenges (deterministic + cross-game) ----- */
const DAILY_POOL = [
  { id: 'daily-orbs-15',   scope: 'moc',   target: 15, verb: 'Collect Focus Orbs', unit: 'orbs', reward: 60,  action: 'Play Mind Over City' },
  { id: 'daily-clear-2',   scope: 'moc',   target: 2,  verb: 'Clear campaign levels', unit: 'clears', reward: 80, action: 'Continue campaign' },
  { id: 'daily-perfect-1', scope: 'moc',   target: 1,  verb: 'Complete a 3-star run', unit: 'perfect runs', reward: 100, action: 'Aim for flow' },
  { id: 'daily-drive-500', scope: 'drive', target: 500, verb: 'Drive metres in Focus Drive', unit: 'm', reward: 60, action: 'Play Focus Drive' },
  { id: 'daily-breath-3',  scope: 'breath',target: 3,  verb: 'Finish breath cycles', unit: 'cycles', reward: 40, action: 'Do Lock In' },
];
const WEEKLY_POOL = [
  { id: 'weekly-orbs-80',   scope: 'moc',   target: 80,   verb: 'Collect Focus Orbs',  unit: 'orbs',    reward: 300, action: 'Practice all week' },
  { id: 'weekly-clear-10',  scope: 'moc',   target: 10,   verb: 'Clear campaign levels',unit: 'clears', reward: 400, action: 'Advance the map' },
  { id: 'weekly-drive-3000',scope: 'drive', target: 3000, verb: 'Drive metres in Focus Drive', unit: 'm', reward: 300, action: 'Long trip' },
  { id: 'weekly-perfect-4', scope: 'moc',   target: 4,    verb: 'Complete 3-star runs', unit: 'perfect runs', reward: 500, action: 'Aim for flow' },
];

function todayKey(d = new Date()) { return d.toISOString().slice(0, 10); }
function isoWeekKey(d = new Date()) {
  // ISO week: Thursday-based
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil((((dt - yearStart) / 86400000) + 1) / 7);
  return `${dt.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}
function hashInt(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) + str.charCodeAt(i);
  return Math.abs(h);
}

function pickChallenge(pool, seedKey) {
  const idx = hashInt(seedKey) % pool.length;
  return { ...pool[idx] };
}

/** Ensure a daily+weekly challenge exists for right now. Rotates on new day/week. */
export function ensureChallenges() {
  const s = read();
  const dKey = todayKey();
  const wKey = isoWeekKey();
  let changed = false;
  if (!s.challenges.daily || s.challenges.daily.key !== dKey) {
    const base = pickChallenge(DAILY_POOL, `d:${dKey}`);
    s.challenges.daily = { ...base, key: dKey, progress: 0, completed: false, rewardClaimed: false };
    changed = true;
  }
  if (!s.challenges.weekly || s.challenges.weekly.key !== wKey) {
    const base = pickChallenge(WEEKLY_POOL, `w:${wKey}`);
    s.challenges.weekly = { ...base, key: wKey, progress: 0, completed: false, rewardClaimed: false };
    changed = true;
  }
  if (changed) write(s);
  return s.challenges;
}

/** Advance challenge progress by `amount` for the given metric. */
function advanceChallenge(scope, metricAmount) {
  const s = read();
  const ch = s.challenges;
  const bumpOne = (c) => {
    if (!c || c.completed) return;
    if (c.scope !== scope) return;
    c.progress = Math.min(c.target, (c.progress ?? 0) + metricAmount);
    if (c.progress >= c.target) c.completed = true;
  };
  bumpOne(ch.daily);
  bumpOne(ch.weekly);
  write(s);
}

/** Claim reward — awards FP, marks claimed. Safe to call twice. */
export function claimChallenge(kind /* 'daily'|'weekly' */) {
  const s = read();
  const c = s.challenges[kind];
  if (!c || !c.completed || c.rewardClaimed) return { ok: false, fpEarned: 0 };
  c.rewardClaimed = true;
  s.fp = Math.max(0, s.fp + (c.reward || 0));
  write(s);
  return { ok: true, fpEarned: c.reward || 0 };
}

/* ----- Achievements catalogue ----- */
export const ACHIEVEMENTS = [
  { id: 'first-drive',        name: 'First Drive',       tier: 'bronze', desc: 'Complete your first Focus Drive run.',
    check: (_st, _rn, _r, g) => g === 'drive' },
  { id: 'first-flight',       name: 'First Flight',      tier: 'bronze', desc: 'Complete your first Mind Over City run.',
    check: (_st, _rn, _r, g) => g === 'flyer' },
  { id: 'first-breath',       name: 'First Breath',      tier: 'bronze', desc: 'Finish one Focus Breath cycle.',
    check: (st) => st.breathCycles >= 1 },
  { id: 'smooth-operator',    name: 'Smooth Operator',   tier: 'silver', desc: 'Complete a perfect run in Focus Drive.',
    check: (_st, _rn, r, g) => g === 'drive' && r.perfect },
  { id: 'calm-under-pressure',name: 'Calm Under Pressure',tier: 'silver',desc: 'Clear Focus Drive level 5.',
    check: (st) => st.highestDriveLevel >= 6 },
  { id: 'night-rider',        name: 'Night Rider',       tier: 'silver', desc: 'Reach 500m in a single Focus Drive run.',
    check: (_st, _rn, r, g) => g === 'drive' && r.distance >= 500 },
  { id: 'focus-champion',     name: 'Focus Champion',    tier: 'gold',   desc: 'Reach 1,000 lifetime Focus Points.',
    check: (st, rn) => (rn[0]?.fpEarned ?? 0) + (st.__fp ?? 0) >= 0 && read().fp >= 1000 },
  { id: 'marathon-driver',    name: 'Marathon Driver',   tier: 'gold',   desc: 'Drive 5,000m in total.',
    check: (st) => st.lifetimeDistance >= 5000 },
  { id: 'city-climber',       name: 'City Climber',      tier: 'gold',   desc: 'Clear Mind Over City level 10.',
    check: (st) => st.highestFlyerLevel >= 11 },
  { id: 'untouchable',        name: 'Untouchable',       tier: 'gold',   desc: 'Log 3 perfect runs.',
    check: (st) => st.perfectRuns >= 3 },
  { id: 'centered',           name: 'Centered',          tier: 'gold',   desc: 'Complete 20 breath cycles total.',
    check: (st) => st.breathCycles >= 20 },
  { id: 'first-defender',     name: 'First Defender',    tier: 'bronze', desc: 'Complete your first Peace Defender wave.',
    check: (_st, _rn, _r, g) => g === 'defender' },
  { id: 'flawless-defender',  name: 'Flawless Defender', tier: 'silver', desc: 'Clear a Peace Defender level without taking a hit.',
    check: (_st, _rn, r, g) => g === 'defender' && r.cleared && (r.hits ?? 0) === 0 },
  { id: 'nebula-master',      name: 'Nebula Master',     tier: 'silver', desc: 'Clear Peace Defender level 10.',
    check: (st) => (st.highestDefenderLevel ?? 1) >= 11 },
  { id: 'prism-bearer',       name: 'Prism Bearer',      tier: 'gold',   desc: 'Clear Peace Defender level 30.',
    check: (st) => (st.highestDefenderLevel ?? 1) >= 30 },
  { id: 'first-command',      name: 'First Command',     tier: 'bronze', desc: 'Complete your first Calm Command wave.',
    check: (_st, _rn, _r, g) => g === 'command' },
  { id: 'unshaken',           name: 'Unshaken',          tier: 'silver', desc: 'Clear a Calm Command wave with all 6 anchors alive.',
    check: (_st, _rn, r, g) => g === 'command' && r.cleared && (r.tokens ?? 0) >= 6 },
  { id: 'field-master',       name: 'Field Master',      tier: 'gold',   desc: 'Clear Calm Command level 30.',
    check: (st) => (st.highestCommandLevel ?? 1) >= 30 },
];

/* ----- Sound-effects hook (files provided by user — graceful fallback) ----- */
// Drop mp3/wav files into /app/frontend/public/audio/sfx/ with these names.
// Every sound below is OPTIONAL. If a file is missing, playSfx() silently no-ops.
// Design principle: subtle & calm. No background music. Ever.
const SFX_URLS = {
  // Existing (Iter 1 Lounge infra)
  engine:      '/audio/sfx/engine.mp3',
  wind:        '/audio/sfx/wind.mp3',
  token:       '/audio/sfx/token.mp3',
  levelup:     '/audio/sfx/levelup.mp3',
  ui:          '/audio/sfx/ui.mp3',
  collision:   '/audio/sfx/collision.mp3',
  // Mind Over City / Brotherhood Arcade shared vocabulary
  orb:         '/audio/sfx/orb.mp3',          // Focus Orb collected
  countdown:   '/audio/sfx/countdown.mp3',    // 3-2-1 tick
  unlock:      '/audio/sfx/unlock.mp3',       // level / mode / cosmetic unlocked
  button:      '/audio/sfx/button.mp3',       // menu press
  fp:          '/audio/sfx/fp.mp3',           // Focus Point earned
  levelClear:  '/audio/sfx/level-clear.mp3',  // level complete jingle (short, subtle)
  star:        '/audio/sfx/star.mp3',         // star awarded
  challenge:   '/audio/sfx/challenge.mp3',    // daily/weekly challenge complete
  // Peace Defender vocabulary
  laser:       '/audio/sfx/laser.mp3',        // ship fires
  boom:        '/audio/sfx/boom.mp3',         // asteroid explodes
  powerup:     '/audio/sfx/powerup.mp3',      // power-up collected
  hit:         '/audio/sfx/hit.mp3',          // player takes damage
};
const SFX_MUTE_KEY = 'lounge_sfx_muted';
export function isSfxMuted() { return localStorage.getItem(SFX_MUTE_KEY) === '1'; }
export function setSfxMuted(v) { localStorage.setItem(SFX_MUTE_KEY, v ? '1' : '0'); window.dispatchEvent(new Event('lounge-meta:change')); }

/* ---------- Web Audio synthesis fallback ----------
 * We prefer real MP3 files when present (drop into /public/audio/sfx/).
 * When a file is missing (404 / decode error), we synthesise the sound
 * on-the-fly using Web Audio primitives. Every sound is tuned to be
 * SUBTLE and CALM (short, low-gain, warm envelopes) so the Lounge
 * still feels like the Lounge — never arcadey or noisy.
 */
let _actx = null;
function _ctx() {
  if (_actx) return _actx;
  try {
    const C = window.AudioContext || window.webkitAudioContext;
    if (C) _actx = new C();
  } catch { _actx = null; }
  return _actx;
}
function _tone({ freq = 440, freq2 = null, type = 'sine', dur = 0.15, gain = 0.12, glide = 0, delay = 0 }) {
  const ctx = _ctx(); if (!ctx) return;
  const t0 = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (freq2 != null) osc.frequency.exponentialRampToValueAtTime(Math.max(1, freq2), t0 + dur);
  else if (glide) osc.frequency.exponentialRampToValueAtTime(Math.max(1, freq + glide), t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}
function _noise({ dur = 0.2, gain = 0.08, hp = 0, lp = 0, delay = 0 }) {
  const ctx = _ctx(); if (!ctx) return;
  const t0 = ctx.currentTime + delay;
  const frames = Math.floor(ctx.sampleRate * dur);
  const buf = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < frames; i++) data[i] = (Math.random() * 2 - 1);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  let node = src;
  if (hp) { const f = ctx.createBiquadFilter(); f.type = 'highpass'; f.frequency.value = hp; node.connect(f); node = f; }
  if (lp) { const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = lp; node.connect(f); node = f; }
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  node.connect(g).connect(ctx.destination);
  src.start(t0);
  src.stop(t0 + dur + 0.02);
}
function _playSynth(name, vol) {
  // vol is the caller's `volume` (0..1) — scales the base gain.
  const V = Math.max(0, Math.min(1, vol));
  switch (name) {
    // ── UI / meta ──
    case 'button':     _tone({ freq: 620, freq2: 500, type: 'sine',     dur: 0.07, gain: 0.10 * V }); break;
    case 'ui':         _tone({ freq: 720, type: 'triangle',            dur: 0.06, gain: 0.09 * V }); break;
    case 'countdown':  _tone({ freq: 880, type: 'sine',                dur: 0.09, gain: 0.11 * V }); break;
    case 'unlock':     _tone({ freq: 523, freq2: 784, type: 'sine',     dur: 0.24, gain: 0.14 * V });
                       _tone({ freq: 659, freq2: 988, type: 'sine',     dur: 0.24, gain: 0.10 * V, delay: 0.06 }); break;
    case 'fp':
    case 'token':
    case 'orb':        _tone({ freq: 987, freq2: 1318, type: 'sine',    dur: 0.11, gain: 0.13 * V }); break;
    case 'star':       _tone({ freq: 1046, freq2: 1568, type: 'sine',   dur: 0.18, gain: 0.14 * V });
                       _tone({ freq: 1318, freq2: 1975, type: 'sine',   dur: 0.16, gain: 0.10 * V, delay: 0.08 }); break;
    case 'levelup':
    case 'levelClear': _tone({ freq: 523, freq2: 659, type: 'triangle', dur: 0.18, gain: 0.14 * V });
                       _tone({ freq: 659, freq2: 784, type: 'triangle', dur: 0.18, gain: 0.12 * V, delay: 0.12 });
                       _tone({ freq: 784, freq2: 1046, type: 'triangle',dur: 0.24, gain: 0.10 * V, delay: 0.24 }); break;
    case 'challenge':  _tone({ freq: 587, freq2: 880, type: 'sine',     dur: 0.20, gain: 0.13 * V });
                       _tone({ freq: 880, freq2: 1174, type: 'sine',    dur: 0.20, gain: 0.10 * V, delay: 0.10 }); break;
    // ── Ambient loops (rendered as short pulses; caller may re-trigger) ──
    case 'engine':     _noise({ dur: 0.30, gain: 0.05 * V, lp: 800 }); break;
    case 'wind':       _noise({ dur: 0.40, gain: 0.04 * V, hp: 3500 }); break;
    // ── Combat / defender ──
    case 'laser':      _tone({ freq: 1400, freq2: 500, type: 'sawtooth',dur: 0.11, gain: 0.10 * V }); break;
    case 'boom':       _noise({ dur: 0.30, gain: 0.16 * V, lp: 500 });
                       _tone({ freq: 90, freq2: 40, type: 'sine',       dur: 0.28, gain: 0.14 * V }); break;
    case 'hit':        _tone({ freq: 200, freq2: 80, type: 'square',    dur: 0.18, gain: 0.12 * V });
                       _noise({ dur: 0.14, gain: 0.06 * V, lp: 900 }); break;
    case 'collision':  _tone({ freq: 260, freq2: 110, type: 'square',   dur: 0.16, gain: 0.11 * V });
                       _noise({ dur: 0.14, gain: 0.06 * V, lp: 1200 }); break;
    case 'powerup':    _tone({ freq: 660, freq2: 990, type: 'sine',     dur: 0.14, gain: 0.13 * V });
                       _tone({ freq: 990, freq2: 1320, type: 'sine',    dur: 0.14, gain: 0.10 * V, delay: 0.07 }); break;
    default:           _tone({ freq: 600, type: 'sine', dur: 0.08, gain: 0.09 * V });
  }
}

const _audioCache = {};
const _fileMissing = {}; // once true, always use synth for this name
export function playSfx(name, { volume = 0.4, loop = false } = {}) {
  if (isSfxMuted()) return null;
  const url = SFX_URLS[name];
  // No URL mapping → synth-only
  if (!url) { _playSynth(name, volume); return null; }
  // Known-missing file → synth
  if (_fileMissing[name]) { _playSynth(name, volume); return null; }
  try {
    let a = _audioCache[name];
    if (!a) {
      a = new Audio(url);
      a.preload = 'auto';
      a.addEventListener('error', () => { _fileMissing[name] = true; });
      _audioCache[name] = a;
    }
    a.currentTime = 0;
    a.volume = volume;
    a.loop = loop;
    const p = a.play();
    if (p && p.catch) {
      p.catch(() => {
        // Autoplay blocked OR the file 404'd. Fall back to synth so
        // the user still hears feedback on the very next click.
        _fileMissing[name] = true;
        _playSynth(name, volume);
      });
    }
    return a;
  } catch {
    _playSynth(name, volume);
    return null;
  }
}
export function stopSfx(name) {
  const a = _audioCache[name];
  if (a) { try { a.pause(); a.currentTime = 0; } catch { /* noop */ } }
}
